/**
 * AsfTabController is a class that manages the behavior of tabs within a tabset.
 * It handles tab selection, activation, and dynamic disabling based on various conditions.
 */
export class AsfTabController {
    /**
     * Constructor for AsfTabController.
     * @param {Object} $parse - AngularJS $parse service.
     * @param {Object} $routeParams - AngularJS $routeParams service.
     * @param {Object} $config - Configuration object.
     * @param {Object} TaskInfoService - Service to fetch task information.
     */
    constructor($parse, $routeParams, $config, TaskInfoService) {
        this.$parse = $parse;
        this.$routeParams = $routeParams;
        this.$config = $config;
        this.TaskInfoService = TaskInfoService;
    }

    /**
     * Link function to initialize the tab behavior.
     * @param {Object} scope - AngularJS scope.
     * @param {Object} elm - Element the directive is applied to.
     * @param {Object} attrs - Attributes of the element.
     * @param {Object} tabsetCtrl - Tabset controller.
     * @param {Function} transclude - Transclude function.
     */
    link(scope, elm, attrs, tabsetCtrl, transclude) {
        const taskInfo = this.TaskInfoService.getTaskInfo();
        scope.disabled = false;
        scope.langISO = taskInfo.getISOInputLanguageCode();

        // Watch for changes in the disable attribute and update the tab's disabled state accordingly.
        if (attrs.disable) {
            scope.$parent.$watch(this.$parse(attrs.disable), function (value) {
                scope.disabled = !!value;
            });
        }

        // Set the index of the tab if not provided.
        if (angular.isUndefined(attrs.index)) {
            if (tabsetCtrl.tabs && tabsetCtrl.tabs.length) {
                scope.index = Math.max.apply(null, tabsetCtrl.tabs.map(function (t) {
                    return t.index;
                })) + 1;
            } else {
                scope.index = 0;
            }
        }

        // Set default classes if not provided.
        if (angular.isUndefined(attrs.classes)) {
            scope.classes = '';
        }

        // Activate the tab if it matches the activeTab condition.
        if (!this.$routeParams.preview && isCMS && angular.isDefined(scope.activeTab) && angular.isDefined(scope.activeTab[scope.containerRef]) && scope.activeTab[scope.containerRef] === scope.tabContent) {
            scope.active = true;
        }

        /**
         * Select function to activate the tab.
         * @param {Event} evt - The event object.
         */
        scope.select = (evt) => {
            if (!scope.disabled) {
                let index = tabsetCtrl.tabs.findIndex(tab => tab.tab === scope);

                tabsetCtrl.select(index, evt);

                if (!this.$routeParams.preview && isCMS && typeof scope.activeTab !== "undefined") {
                    scope.activeTab[this.containerRef] = scope.tabContent;
                }
            }
        };

        // Watch for changes in the active state and update the tab accordingly.
        scope.$watch('active', function (active) {
            if (active) {
                scope.select();
            }
            if (scope.tab) {
                scope.tab.active = active;
            }
        });

        // Add the tab to the tabset controller.
        tabsetCtrl.addTab(scope);

        // Remove the tab from the tabset controller when the scope is destroyed.
        scope.$on('$destroy', function () {
            tabsetCtrl.removeTab(scope);
        });

        // Set the transclude function for the scope.
        scope.$transcludeFn = transclude;
    }
}

AsfTabController.$inject = ['$parse', '$routeParams', '$config', 'TaskInfoService'];

/**
 * Registers the asfTabHeadingTransclude directive.
 * @param {Object} module - AngularJS module.
 */
export function registerAsfTabHeadingTranscludeDirective(module) {
    module.directive('asfTabHeadingTransclude', [function() {
        return {
            restrict: 'A',
            require: '^asf-Tab',
            link: function(scope, elm, attrs, tabCtrl) {
                scope.$watch('headingElement', function updateHeadingElement(heading) {
                    if (heading) {
                        elm.html('');
                        elm.append(heading);
                    }
                });
            }
        };
    }]);
}

/**
 * Registers the asfTab directive.
 * @param {Object} module - AngularJS module.
 */
export function registerAsfTabDirective(module) {
    module.directive('asfTab', ['$parse', '$routeParams', '$config', 'TaskInfoService', function($parse, $routeParams, $config, TaskInfoService) {
        return {
            require: '^uibTabset',
            restrict: 'EA',
            replace: true,
            templateUrl: function() {
                if (!$config.isPreviewMode() && isCMS) {
                    return '/views/ib/components/tab/TabDirectiveCMS.html';
                }
                return '/views/ib/components/tab/TabDirective.html';
            },
            transclude: true,
            scope: {
                heading: '@',
                popoverText: '@',
                onSelect: '&select',
                onDeselect: '&deselect',
                tabContent: '@',
                rowKey: '=',
                colKey: '=',
                containerKey: '=',
                onDeleteTab: '=',
                tab: '=',
                tabLength: '@',
                activeTab: '=',
                containerRef: '@',
                tabKey: '@',
                isReadingTime: '=',
                langISO: '@'
            },
            controller: AsfTabController,
            link: function(scope, elm, attrs, tabsetCtrl, transclude) {
                const controller = new AsfTabController($parse, $routeParams, $config, TaskInfoService);
                controller.link(scope, elm, attrs, tabsetCtrl, transclude);
            }
        };
    }]);
}